import { FunctionComponent, useDeferredValue, useMemo } from 'react';
import { ICoordinatingGroup, ICountry, IInstitution, IMasterGroup, ITrial, ISourceDataConfigurationSummary } from '@/api/dtos';
import { Divider, List, LinearProgress, Box, styled } from '@mui/material'
import ConfigurationSummaryListItemSkeleton from './ConfigurationSummaryListItemSkeleton';
import ConfigurationSummaryListItem from './ConfigurationSummaryListItem';
import { Virtuoso } from 'react-virtuoso';
import { BasicAlert } from '@ngt/forms';
import { faExclamationTriangle } from '@fortawesome/duotone-light-svg-icons';
import OverlayLoading from '@/components/common/overlayLoading/OverlayLoading';

const ProgressContainer = styled('div')({
    position: 'relative'
});

const DataProgress = styled(LinearProgress)({
    position: 'absolute',
    left: 0,
    right: 0
});

interface IConfigurationSummaryList {
    search?: string;
    trial?: ITrial;
    summaries?: ISourceDataConfigurationSummary[];
    loading?: boolean;
    updating?: boolean;
    skeletonCount?: number;
    linkFn?: (trial?: ITrial, summary?: ISourceDataConfigurationSummary) => string;
}

const ConfigurationSummaryList: FunctionComponent<IConfigurationSummaryList> = ({
    summaries,
    trial,
    loading,
    updating,
    skeletonCount,
    linkFn,
    search
}) => {
    const loadingArray = useMemo(() => {
        return Array(skeletonCount ?? 20).fill(0);
    }, [skeletonCount])

    const filteredList = useMemo(() => {
        if (!search) {
            return summaries;
        }
        
        const lowerCaseSearch = search.toLowerCase();

        return summaries?.filter(x => x.name?.toLowerCase().includes(lowerCaseSearch) || x.description?.toLowerCase().includes(lowerCaseSearch))
    }, [summaries, search])

    return (
        <>
             {loading ? (
                <OverlayLoading />
            ) : (
            <>
                {
                    updating && (
                        <ProgressContainer>
                            <DataProgress
                                color="secondary"
                            />
                        </ProgressContainer>
                    )
                }
                <List disablePadding>
                    {
                        (loading || !!summaries?.length) && (
                            <Virtuoso
                                useWindowScroll
                                data={loading ? loadingArray : (filteredList ?? [])}
                                itemContent={(index, summary) => {
                                    return (
                                        <>
                                            <ConfigurationSummaryListItem  
                                                summary={summary}
                                            trial={trial}
                                            linkFn={linkFn}
                                        />
                                        { index < ((summaries?.length ?? 0) - 1) && <Divider />}
                                    </>
                                )
                            }}
                        />
                    )
                }
                {
                    (!loading && !summaries?.length) && (
                        <BasicAlert
                            title="No Configurations"
                            description="No source data configurations have been created for the selected trial."
                            severity="warning"
                            icon={faExclamationTriangle}
                        />
                    )
                }
            </List>
        </>
        )}    
    </>
    );
}

export default ConfigurationSummaryList;